
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface MiniProfileBoxProps {
  profile: any;
}

const MiniProfileBox: React.FC<MiniProfileBoxProps> = ({ profile }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleProfileClick = () => {
    if (user) {
      // If user is logged in, navigate to the profile page with the correct route format
      navigate(`/profile/${profile.username}`);
    } else {
      // If user is not logged in, navigate to home page
      navigate('/home');
    }
  };

  return (
    <div 
      className="relative h-full w-full group cursor-pointer" 
      onClick={handleProfileClick}
    >
      {/* Background with banner image */}
      {profile.banner_image_url && (
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${profile.banner_image_url})` }}
        />
      )}
      
      {/* Content overlay */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-2 text-white">
        <Avatar className={cn(
          "w-12 h-12 mb-1", // Further reduced avatar size for better fit
          profile?.is_admin && "ring-2 ring-[#F97316]",
          profile?.is_premium && "ring-2 ring-[#E5DEFF]",
          !profile?.is_admin && !profile?.is_premium && "ring-2 ring-primary/20"
        )}>
          <AvatarImage 
            src={profile.profile_image_url} 
            alt={profile.display_name || profile.username}
          />
          <AvatarFallback>
            {(profile.display_name?.charAt(0) || profile.username?.charAt(0) || "U").toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <h2 className="text-xs font-bold mb-0.5 text-balance truncate max-w-[90%]">
          {profile.display_name || profile.username}
        </h2>
        <p className="text-[0.65rem] text-white/70 mb-0.5 truncate max-w-[90%]">@{profile.username}</p>
        
        {profile.bio && (
          <p className="text-[0.65rem] text-white/80 text-center mb-0.5 max-w-[95%] text-balance line-clamp-2">
            {profile.bio}
          </p>
        )}
        
        {profile.favorite_quote && (
          <p className="text-[0.6rem] text-white/60 italic text-center max-w-[95%] text-balance line-clamp-1">
            "{profile.favorite_quote}"
          </p>
        )}
      </div>
    </div>
  );
}

export default MiniProfileBox;
